import { useEffect, useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';

import { api } from '../api/client';
import { useRouters } from '../api/queries';
import type { PanelUser, PanelUserDetail, Role } from '../api/types';
import { useAuth } from '../auth/auth-context';
import { RoleBadge } from '../components/status';
import { Card, ConfirmDialog, Empty, ErrorAlert, Field, Modal, Spinner } from '../components/ui';
import { formatRelative } from '../lib/format';

export function PanelUsersPage() {
  const { hasRole, profile } = useAuth();
  const queryClient = useQueryClient();

  const [creating, setCreating] = useState(false);
  const [editing, setEditing] = useState<PanelUser | null>(null);
  const [accessFor, setAccessFor] = useState<PanelUser | null>(null);
  const [deleting, setDeleting] = useState<PanelUser | null>(null);
  const [pageError, setPageError] = useState<unknown>(null);

  const users = useQuery({
    queryKey: ['panel-users'],
    queryFn: () => api.get<PanelUser[]>('/users'),
  });

  const invalidate = () => void queryClient.invalidateQueries({ queryKey: ['panel-users'] });

  const remove = useMutation({
    mutationFn: (user: PanelUser) => api.del<void>(`/users/${user.id}`),
    onSuccess: () => {
      setDeleting(null);
      invalidate();
    },
    onError: (error) => setPageError(error),
  });

  return (
    <main className="page">
      <div className="page-header">
        <div>
          <h1>Usuarios del panel</h1>
          <p>
            Quién puede administrar MikroManager. El permiso efectivo es el rol combinado con los
            routers asignados.
          </p>
        </div>
        <button type="button" className="primary" onClick={() => setCreating(true)}>
          Nuevo usuario
        </button>
      </div>

      <ErrorAlert error={pageError} onDismiss={() => setPageError(null)} />

      <Card>
        {users.isLoading ? (
          <div className="card-body">
            <Spinner />
          </div>
        ) : users.data && users.data.length > 0 ? (
          <div className="table-wrap">
            <table>
              <thead>
                <tr>
                  <th>Nombre</th>
                  <th>Email</th>
                  <th>Rol</th>
                  <th>Estado</th>
                  <th className="num">Routers</th>
                  <th className="num">Usuarios creados</th>
                  <th>Último acceso</th>
                  <th className="actions" />
                </tr>
              </thead>
              <tbody>
                {users.data.map((user) => (
                  <tr key={user.id}>
                    <td>
                      <strong>{user.name}</strong>
                      {user.id === profile?.id && (
                        <span className="badge info" style={{ marginLeft: 6 }}>
                          tú
                        </span>
                      )}
                    </td>
                    <td className="mono">{user.email}</td>
                    <td>
                      <RoleBadge role={user.role} />
                    </td>
                    <td>
                      {user.isActive ? (
                        <span className="badge ok">activo</span>
                      ) : (
                        <span className="badge bad">desactivado</span>
                      )}
                    </td>
                    <td className="num">
                      {user.role === 'SUPER_ADMIN' ? (
                        <span className="faint">todos</span>
                      ) : (
                        (user.routerCount ?? 0)
                      )}
                    </td>
                    <td className="num">{user.hotspotUserCount ?? 0}</td>
                    <td className="faint">{formatRelative(user.lastLoginAt)}</td>
                    <td className="actions">
                      <button type="button" className="small" onClick={() => setEditing(user)}>
                        Editar
                      </button>
                      {user.role !== 'SUPER_ADMIN' && (
                        <button
                          type="button"
                          className="small"
                          style={{ marginLeft: 6 }}
                          onClick={() => setAccessFor(user)}
                        >
                          Routers
                        </button>
                      )}
                      {hasRole('SUPER_ADMIN') && user.id !== profile?.id && (
                        <button
                          type="button"
                          className="small danger"
                          style={{ marginLeft: 6 }}
                          onClick={() => setDeleting(user)}
                        >
                          Eliminar
                        </button>
                      )}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        ) : (
          <Empty>No hay usuarios del panel.</Empty>
        )}
      </Card>

      {(creating || editing) && (
        <UserFormModal
          user={editing}
          onClose={() => {
            setCreating(false);
            setEditing(null);
          }}
          onSaved={() => {
            setCreating(false);
            setEditing(null);
            invalidate();
          }}
        />
      )}

      {accessFor && (
        <RouterAccessModal
          user={accessFor}
          onClose={() => setAccessFor(null)}
          onSaved={() => {
            setAccessFor(null);
            invalidate();
          }}
        />
      )}

      {deleting && (
        <ConfirmDialog
          title={`Eliminar ${deleting.name}`}
          danger
          busy={remove.isPending}
          confirmLabel="Eliminar usuario"
          message={
            <p>
              Los usuarios HotSpot que creó y sus entradas de auditoría se conservan: la relación
              queda sin dueño pero el rastro de quién hizo qué no se pierde.
            </p>
          }
          onCancel={() => setDeleting(null)}
          onConfirm={() => remove.mutate(deleting)}
        />
      )}
    </main>
  );
}

function UserFormModal({
  user,
  onClose,
  onSaved,
}: {
  user: PanelUser | null;
  onClose: () => void;
  onSaved: () => void;
}) {
  const [name, setName] = useState(user?.name ?? '');
  const [email, setEmail] = useState(user?.email ?? '');
  const [password, setPassword] = useState('');
  const [role, setRole] = useState<Role>(user?.role ?? 'OPERATOR');
  const [isActive, setIsActive] = useState(user?.isActive ?? true);
  const [error, setError] = useState<unknown>(null);

  const assignableRoles: Role[] = ['OPERATOR', 'SUPER_ADMIN'];

  const save = useMutation({
    mutationFn: () => {
      const payload = {
        name,
        email,
        role,
        isActive,
        ...(password ? { password } : {}),
      };
      return user ? api.patch<PanelUser>(`/users/${user.id}`, payload) : api.post<PanelUser>('/users', payload);
    },
    onSuccess: onSaved,
    onError: (caught) => setError(caught),
  });

  return (
    <Modal
      title={user ? `Editar ${user.name}` : 'Nuevo usuario del panel'}
      onClose={onClose}
      footer={
        <>
          <button type="button" onClick={onClose}>
            Cancelar
          </button>
          <button
            type="button"
            className="primary"
            disabled={save.isPending || (!user && !password)}
            onClick={() => save.mutate()}
          >
            {save.isPending ? <Spinner label="Guardando…" /> : 'Guardar'}
          </button>
        </>
      }
    >
      <ErrorAlert error={error} onDismiss={() => setError(null)} />

      <div className="form-row">
        <Field label="Nombre">
          <input value={name} onChange={(event) => setName(event.target.value)} />
        </Field>
        <Field label="Email">
          <input type="email" value={email} onChange={(event) => setEmail(event.target.value)} />
        </Field>
      </div>

      <Field
        label="Contraseña"
        hint={
          user
            ? 'Vacío = mantener la actual. Cambiarla cierra todas las sesiones del usuario.'
            : 'Mínimo 10 caracteres'
        }
      >
        <input
          type="password"
          value={password}
          onChange={(event) => setPassword(event.target.value)}
          autoComplete="new-password"
        />
      </Field>

      <Field
        label="Rol"
        hint={
          role === 'SUPER_ADMIN'
            ? 'Acceso total, incluidos todos los routers de forma implícita.'
            : 'Crea y elimina usuarios, vouchers y clientes, y ve las conexiones de sus routers.'
        }
      >
        <select value={role} onChange={(event) => setRole(event.target.value as Role)}>
          {assignableRoles.map((option) => (
            <option key={option} value={option}>
              {option === 'SUPER_ADMIN' ? 'Super admin' : 'Operador'}
            </option>
          ))}
        </select>
      </Field>

      <label className="inline">
        <input type="checkbox" checked={isActive} onChange={(event) => setIsActive(event.target.checked)} />
        Activo
      </label>
    </Modal>
  );
}

interface AccessRow {
  routerId: string;
  enabled: boolean;
  canCreate: boolean;
  canDelete: boolean;
  canSeeAllUsers: boolean;
}

function RouterAccessModal({
  user,
  onClose,
  onSaved,
}: {
  user: PanelUser;
  onClose: () => void;
  onSaved: () => void;
}) {
  const routers = useRouters();
  const [error, setError] = useState<unknown>(null);
  const [rows, setRows] = useState<AccessRow[] | null>(null);

  const detail = useQuery({
    queryKey: ['panel-user', user.id],
    queryFn: () => api.get<PanelUserDetail>(`/users/${user.id}`),
  });

  // El estado editable se construye una sola vez, cuando ya están el detalle del usuario y
  // la lista de routers. Después no se resincroniza: sobrescribir lo que el operador está
  // marcando porque llegó un refetch de fondo sería peor que quedarse con datos de hace
  // unos segundos.
  const accessLoaded = detail.data !== undefined && routers.data !== undefined;
  useEffect(() => {
    if (!accessLoaded || rows !== null) return;
    const existing = new Map((detail.data?.routerAccess ?? []).map((entry) => [entry.routerId, entry]));
    setRows(
      (routers.data ?? []).map((router) => {
        const entry = existing.get(router.id);
        return {
          routerId: router.id,
          enabled: Boolean(entry),
          canCreate: entry?.canCreate ?? true,
          canDelete: entry?.canDelete ?? false,
          canSeeAllUsers: entry?.canSeeAllUsers ?? false,
        };
      }),
    );
  }, [accessLoaded, rows, detail.data, routers.data]);

  const update = (routerId: string, patch: Partial<AccessRow>) =>
    setRows((current) =>
      (current ?? []).map((row) => (row.routerId === routerId ? { ...row, ...patch } : row)),
    );

  const save = useMutation({
    mutationFn: () =>
      api.put<PanelUserDetail>(`/users/${user.id}/router-access`, {
        access: (rows ?? [])
          .filter((row) => row.enabled)
          .map((row) => ({
            routerId: row.routerId,
            canCreate: row.canCreate,
            canDelete: row.canDelete,
            canSeeAllUsers: row.canSeeAllUsers,
          })),
      }),
    onSuccess: onSaved,
    onError: (caught) => setError(caught),
  });

  return (
    <Modal
      wide
      title={`Routers de ${user.name}`}
      onClose={onClose}
      footer={
        <>
          <button type="button" onClick={onClose}>
            Cancelar
          </button>
          <button type="button" className="primary" disabled={save.isPending} onClick={() => save.mutate()}>
            {save.isPending ? <Spinner label="Guardando…" /> : 'Guardar accesos'}
          </button>
        </>
      }
    >
      <ErrorAlert error={error} onDismiss={() => setError(null)} />

      <p className="muted" style={{ marginTop: 0 }}>
        Sin acceso a un router, este usuario recibe 403 en cualquier operación sobre él, incluida la
        lectura.
      </p>

      {!rows ? (
        <Spinner />
      ) : rows.length === 0 ? (
        <Empty>No hay routers registrados todavía.</Empty>
      ) : (
        <div className="table-wrap">
          <table>
            <thead>
              <tr>
                <th>Router</th>
                <th>Acceso</th>
                <th>Crear</th>
                <th>Eliminar / desconectar</th>
                <th>Ver todos los usuarios</th>
              </tr>
            </thead>
            <tbody>
              {rows.map((row) => {
                const router = routers.data?.find((item) => item.id === row.routerId);
                return (
                  <tr key={row.routerId}>
                    <td>
                      <strong>{router?.name}</strong>
                      <div className="faint mono" style={{ fontSize: 12 }}>
                        {router?.host}
                      </div>
                    </td>
                    <td>
                      <input
                        type="checkbox"
                        checked={row.enabled}
                        onChange={(event) => update(row.routerId, { enabled: event.target.checked })}
                      />
                    </td>
                    <td>
                      <input
                        type="checkbox"
                        disabled={!row.enabled}
                        checked={row.canCreate}
                        onChange={(event) => update(row.routerId, { canCreate: event.target.checked })}
                      />
                    </td>
                    <td>
                      <input
                        type="checkbox"
                        disabled={!row.enabled}
                        checked={row.canDelete}
                        onChange={(event) => update(row.routerId, { canDelete: event.target.checked })}
                      />
                    </td>
                    <td>
                      <input
                        type="checkbox"
                        disabled={!row.enabled}
                        checked={row.canSeeAllUsers}
                        onChange={(event) => update(row.routerId, { canSeeAllUsers: event.target.checked })}
                      />
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      )}
    </Modal>
  );
}
